import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { collection, query, where, onSnapshot, doc, getDoc } from 'firebase/firestore';
import { db } from '../services/firebase';
import { useCart } from '../contexts/CartContext';
import Header from '../components/Header';
import Footer from '../components/Footer';

const ProductDetailPage = () => {
  const { id } = useParams(); // This will now be the product name from URL
  const navigate = useNavigate();
  const { addToCart } = useCart();
  
  // State for products from Firestore
  const [product, setProduct] = useState(null);
  const [allProducts, setAllProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  // Fetch specific product and all products for recommendations
  useEffect(() => {
    const fetchProduct = async () => {
      try {
        // Convert URL name back to readable format
        const productName = id.replace(/-/g, ' ');
        
        // Get all active products
        const q = query(
          collection(db, 'catalogueProducts'),
          where('status', '==', 'active')
        );

        const unsubscribe = onSnapshot(q, (snapshot) => {
          const productsData = snapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          }));
          
          // Find the specific product by name (case-insensitive)
          const foundProduct = productsData.find(product => 
            product.name.toLowerCase() === productName.toLowerCase()
          );
          
          if (foundProduct) {
            setProduct(foundProduct);
          }
          
          setAllProducts(productsData);
          setLoading(false);
        });

        return unsubscribe;
      } catch (error) {
        console.error('Error fetching product:', error);
        setLoading(false);
      }
    };

    if (id) {
      fetchProduct();
    }
  }, [id]);

  
  // States for product options
  const [selectedWeight, setSelectedWeight] = useState('');
  const [selectedType, setSelectedType] = useState('');
  const [quantity, setQuantity] = useState(1);
  const [activeTab, setActiveTab] = useState('aboutBeans');
  const [isMobile, setIsMobile] = useState(false);
  const [showNotification, setShowNotification] = useState(false);

  // Check if mobile
  useEffect(() => {
    const checkIsMobile = () => setIsMobile(window.innerWidth <= 768);
    checkIsMobile();
    window.addEventListener('resize', checkIsMobile);
    return () => window.removeEventListener('resize', checkIsMobile);
  }, []);

  // Scroll to top when component mounts or ID changes
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [id]);

  // Get dynamic options from product data
  const weights = product?.weightOptions?.map(option => option.weight) || ['250'];
  const types = product?.typeOptions?.map(option => option.type) || ['Filter'];

  // Set default selections when product loads
  useEffect(() => {
    if (product) {
      if (!selectedWeight && product.weightOptions?.length > 0) {
        setSelectedWeight(product.weightOptions[0].weight);
      }
      if (!selectedType && product.typeOptions?.length > 0) {
        setSelectedType(product.typeOptions[0].type);
      }
    }
  }, [product, selectedWeight, selectedType]);

  // Calculate current price based on selections
  const getCurrentPrice = () => {
    if (product.weightOptions && selectedWeight) {
      const weightOption = product.weightOptions.find(opt => opt.weight === selectedWeight);
      if (weightOption && weightOption.price) {
        return parseFloat(weightOption.price);
      }
    }
    
    if (product.typeOptions && selectedType) {
      const typeOption = product.typeOptions.find(opt => opt.type === selectedType);
      if (typeOption && typeOption.price) {
        return parseFloat(typeOption.price);
      }
    }
    
    return parseFloat(product.price || 0);
  };

  // Handle add to cart
  const handleAddToCart = () => {
    if (!product) return;

    // Validate required selections
    if (product.weightOptions?.length > 0 && !selectedWeight) return;
    if (product.typeOptions?.length > 0 && !selectedType) return;

    const currentPrice = getCurrentPrice();
    
    const cartItem = {
      id: product.id,
      name: product.name,
      image: product.image,
      price: currentPrice,
      quantity: quantity,
      selectedWeight: selectedWeight || 'standard',
      selectedBeanType: selectedType || 'default',
      origin: product.origin,
      producer: product.producer,
      category: product.category,
      weightDisplay: selectedWeight ? `${selectedWeight}${product.weightOptions?.find(opt => opt.weight === selectedWeight)?.unit || ''}` : 'Standard',
      typeDisplay: selectedType || 'Default'
    };

    addToCart(cartItem);
    
    // Show notification
    setShowNotification(true);
    setTimeout(() => {
      setShowNotification(false);
    }, 3000);
  };

  // Loading state
  if (loading) {
    return (
      <div style={{ minHeight: '100vh', background: 'white', display: 'flex', flexDirection: 'column' }}>
        <Header />
        <main style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ 
            textAlign: 'center',
            fontSize: '18px',
            letterSpacing: '2px',
            textTransform: 'uppercase'
          }}>
            Loading product...
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  if (!product) {
    return (
      <div style={{ minHeight: '100vh', background: 'white', display: 'flex', flexDirection: 'column' }}>
        <Header />
        <main style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ textAlign: 'center' }}>
            <h2 style={{ fontSize: '24px', marginBottom: '16px' }}>Product Not Found</h2>
            <button 
              onClick={() => navigate('/catalogue')}
              style={{
                background: '#000',
                color: 'white',
                border: '1px solid #000',
                padding: '12px 24px',
                fontSize: '14px',
                letterSpacing: '1px',
                textTransform: 'uppercase',
                cursor: 'pointer'
              }}
            >
              Back to Catalogue
            </button>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: 'white', display: 'flex', flexDirection: 'column' }}>
      {/* CSS for notification animation */}
      <style>
        {`
          @keyframes slideDown {
            from {
              transform: translate(-50%, -100%);
              opacity: 0;
            }
            to {
              transform: translate(-50%, 0);
              opacity: 1;
            }
          }
        `}
      </style>
      
      {/* Notification Toast */}
      {showNotification && (
        <div style={{
          position: 'fixed',
          top: '80px', // Just under the header
          left: '50%',
          transform: 'translateX(-50%)',
          background: '#000',
          color: 'white',
          padding: isMobile ? '15px 30px' : '12px 25px',
          border: '1px solid #000',
          fontSize: isMobile ? '12px' : '11px',
          letterSpacing: '1px',
          textTransform: 'uppercase',
          zIndex: 1000,
          animation: 'slideDown 0.3s ease-out',
          textAlign: 'center',
          whiteSpace: 'nowrap'
        }}>
          ✓ Added to Cart: {product.name}
        </div>
      )}
      
      <Header />
      
      <main style={{ flex: 1, padding: '40px 20px' }}>
        <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
          {/* Back Button */}
          <button
            onClick={() => navigate('/catalogue')}
            style={{
              background: 'transparent',
              border: '1px solid #000',
              padding: '12px 24px',
              fontSize: '12px',
              letterSpacing: '1px',
              textTransform: 'uppercase',
              cursor: 'pointer',
              marginBottom: '30px'
            }}
          >
            ← Back to Catalogue
          </button>

          {/* Three Section Layout - Responsive */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr 1fr',
            gap: '1px',
            border: '1px solid #000',
            minHeight: isMobile ? 'auto' : '500px'
          }}>
            
            {/* Left Section - Product Details */}
            <div style={{
              background: 'white',
              borderRight: isMobile ? 'none' : '1px solid #000',
              borderBottom: isMobile ? '1px solid #000' : 'none',
              padding: isMobile ? '20px' : '30px'
            }}>
              {/* Tab Navigation */}
              <div style={{
                borderBottom: '1px solid #000',
                marginBottom: '20px',
                paddingBottom: '15px'
              }}>
                <button
                  onClick={() => setActiveTab('aboutBeans')}
                  style={{
                    background: activeTab === 'aboutBeans' ? '#000' : 'transparent',
                    color: activeTab === 'aboutBeans' ? 'white' : '#000',
                    border: '1px solid #000',
                    padding: isMobile ? '8px 12px' : '12px 20px',
                    fontSize: isMobile ? '10px' : '12px',
                    letterSpacing: '1px',
                    textTransform: 'uppercase',
                    cursor: 'pointer',
                    marginRight: '10px',
                    marginBottom: isMobile ? '10px' : '0'
                  }}
                >
                  About Beans
                </button>
                <button
                  onClick={() => setActiveTab('producer')}
                  style={{
                    background: activeTab === 'producer' ? '#000' : 'transparent',
                    color: activeTab === 'producer' ? 'white' : '#000',
                    border: '1px solid #000',
                    padding: isMobile ? '8px 12px' : '12px 20px',
                    fontSize: isMobile ? '10px' : '12px',
                    letterSpacing: '1px',
                    textTransform: 'uppercase',
                    cursor: 'pointer'
                  }}
                >
                  Producer
                </button>
              </div>

              {/* Tab Content */}
              {activeTab === 'aboutBeans' && (
                <div>
                  <h3 style={{
                    fontSize: '16px',
                    fontWeight: '600',
                    letterSpacing: '1px',
                    textTransform: 'uppercase',
                    marginBottom: '20px',
                    borderBottom: '1px solid #000',
                    paddingBottom: '10px'
                  }}>
                    About Beans:
                  </h3>
                  
                  <div style={{ lineHeight: '2' }}>
                    <div><strong>Origin:</strong> {product.origin || 'N/A'}</div>
                    <div><strong>Altitude:</strong> {product.altitude || 'N/A'}</div>
                    <div><strong>Process:</strong> {product.process || 'N/A'}</div>
                    <div><strong>Varietal:</strong> {product.varietal || 'N/A'}</div>
                    <div><strong>Notes:</strong> {product.notes || 'N/A'}</div>
                  </div>
                </div>
              )}

              {activeTab === 'producer' && (
                <div>
                  <h3 style={{
                    fontSize: '16px',
                    fontWeight: '600',
                    letterSpacing: '1px',
                    textTransform: 'uppercase',
                    marginBottom: '20px',
                    borderBottom: '1px solid #000',
                    paddingBottom: '10px'
                  }}>
                    Producer:
                  </h3>
                  
                  <div style={{ lineHeight: '2' }}>
                    <div><strong>Producer:</strong> {product.producer || 'N/A'}</div>
                    <div><strong>Region:</strong> {product.region || 'N/A'}</div>
                    <div><strong>Story:</strong> {product.story || 'Quality products from our curated selection'}</div>
                  </div>
                </div>
              )}
            </div>

            {/* Center Section - Product Image */}
            <div style={{
              background: 'white',
              borderRight: isMobile ? 'none' : '1px solid #000',
              borderBottom: isMobile ? '1px solid #000' : 'none',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: isMobile ? '20px' : '30px',
              minHeight: isMobile ? '250px' : 'auto'
            }}>
              <img
                src={activeTab === 'producer' ? product.producerImage : product.image}
                alt={activeTab === 'producer' ? 'Coffee producer' : product.name}
                style={{
                  maxWidth: '100%',
                  maxHeight: '100%',
                  objectFit: 'contain'
                }}
              />
            </div>

            {/* Right Section - Purchase Options */}
            <div style={{
              background: 'white',
              padding: isMobile ? '20px' : '30px'
            }}>
              <h2 style={{
                fontSize: isMobile ? '18px' : '24px',
                fontWeight: '300',
                letterSpacing: isMobile ? '1px' : '2px',
                textTransform: 'uppercase',
                marginBottom: isMobile ? '8px' : '10px'
              }}>
                {product.name}
              </h2>
              
              <div style={{
                fontSize: isMobile ? '24px' : '28px',
                fontWeight: '600',
                marginBottom: isMobile ? '20px' : '30px',
                borderBottom: '1px solid #000',
                paddingBottom: isMobile ? '15px' : '15px'
              }}>
                {(() => {
                  // Calculate price based on selected weight and type
                  if (product.weightOptions && selectedWeight) {
                    const weightOption = product.weightOptions.find(opt => opt.weight === selectedWeight);
                    if (weightOption && weightOption.price) {
                      return `${weightOption.price} QAR`;
                    }
                  }
                  
                  if (product.typeOptions && selectedType) {
                    const typeOption = product.typeOptions.find(opt => opt.type === selectedType);
                    if (typeOption && typeOption.price) {
                      return `${typeOption.price} QAR`;
                    }
                  }
                  
                  return `${product.price || 'N/A'} QAR`;
                })()}
              </div>

              {/* Weight Selection */}
              <div style={{ marginBottom: isMobile ? '20px' : '25px' }}>
                <label style={{
                  display: 'block',
                  fontSize: isMobile ? '10px' : '12px',
                  fontWeight: '600',
                  letterSpacing: '1px',
                  textTransform: 'uppercase',
                  marginBottom: '10px'
                }}>
                  Weight
                </label>
                <div style={{ 
                  display: 'flex', 
                  gap: isMobile ? '5px' : '10px',
                  flexWrap: 'wrap'
                }}>
                  {product?.weightOptions?.map(option => (
                    <button
                      key={`${option.weight}${option.unit}`}
                      onClick={() => setSelectedWeight(option.weight)}
                      style={{
                        background: selectedWeight === option.weight ? '#000' : 'transparent',
                        color: selectedWeight === option.weight ? 'white' : '#000',
                        border: '1px solid #000',
                        padding: isMobile ? '8px 12px' : '10px 15px',
                        fontSize: isMobile ? '12px' : '14px',
                        cursor: 'pointer',
                        flex: isMobile ? '1' : 'none'
                      }}
                    >
                      {option.weight}{option.unit}
                    </button>
                  )) || (
                    <button
                      style={{
                        background: '#000',
                        color: 'white',
                        border: '1px solid #000',
                        padding: isMobile ? '8px 12px' : '10px 15px',
                        fontSize: isMobile ? '12px' : '14px',
                        cursor: 'pointer'
                      }}
                    >
                      250g
                    </button>
                  )}
                </div>
              </div>

              {/* Type Selection */}
              <div style={{ marginBottom: isMobile ? '20px' : '25px' }}>
                <label style={{
                  display: 'block',
                  fontSize: isMobile ? '10px' : '12px',
                  fontWeight: '600',
                  letterSpacing: '1px',
                  textTransform: 'uppercase',
                  marginBottom: '10px'
                }}>
                  Type
                </label>
                <div style={{ 
                  display: 'flex', 
                  gap: isMobile ? '5px' : '10px',
                  flexWrap: 'wrap'
                }}>
                  {product?.typeOptions?.map(option => (
                    <button
                      key={option.type}
                      onClick={() => setSelectedType(option.type)}
                      style={{
                        background: selectedType === option.type ? '#000' : 'transparent',
                        color: selectedType === option.type ? 'white' : '#000',
                        border: '1px solid #000',
                        padding: isMobile ? '8px 12px' : '10px 15px',
                        fontSize: isMobile ? '12px' : '14px',
                        cursor: 'pointer',
                        flex: isMobile ? '1' : 'none'
                      }}
                    >
                      {option.type}
                    </button>
                  )) || (
                    <button
                      style={{
                        background: '#000',
                        color: 'white',
                        border: '1px solid #000',
                        padding: isMobile ? '8px 12px' : '10px 15px',
                        fontSize: isMobile ? '12px' : '14px',
                        cursor: 'pointer'
                      }}
                    >
                      Filter
                    </button>
                  )}
                </div>
              </div>

              {/* Quantity Selection */}
              <div style={{ marginBottom: isMobile ? '20px' : '30px' }}>
                <label style={{
                  display: 'block',
                  fontSize: isMobile ? '10px' : '12px',
                  fontWeight: '600',
                  letterSpacing: '1px',
                  textTransform: 'uppercase',
                  marginBottom: '10px'
                }}>
                  Quantity
                </label>
                <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? '10px' : '15px' }}>
                  <button
                    onClick={() => setQuantity(Math.max(1, quantity - 1))}
                    style={{
                      background: 'transparent',
                      border: '1px solid #000',
                      width: isMobile ? '35px' : '40px',
                      height: isMobile ? '35px' : '40px',
                      fontSize: isMobile ? '16px' : '18px',
                      cursor: 'pointer'
                    }}
                  >
                    −
                  </button>
                  <span style={{ 
                    fontSize: isMobile ? '16px' : '18px', 
                    fontWeight: '600', 
                    minWidth: '20px', 
                    textAlign: 'center' 
                  }}>
                    {quantity}
                  </span>
                  <button
                    onClick={() => setQuantity(quantity + 1)}
                    style={{
                      background: 'transparent',
                      border: '1px solid #000',
                      width: isMobile ? '35px' : '40px',
                      height: isMobile ? '35px' : '40px',
                      fontSize: isMobile ? '16px' : '18px',
                      cursor: 'pointer'
                    }}
                  >
                    +
                  </button>
                </div>
              </div>

              {/* Add to Cart Button */}
              <button
                onClick={handleAddToCart}
                disabled={
                  (product?.weightOptions?.length > 0 && !selectedWeight) || 
                  (product?.typeOptions?.length > 0 && !selectedType)
                }
                style={{
                  width: '100%',
                  background: (
                    (product?.weightOptions?.length > 0 && !selectedWeight) || 
                    (product?.typeOptions?.length > 0 && !selectedType)
                  ) ? '#ccc' : '#000',
                  color: 'white',
                  border: '1px solid #000',
                  padding: isMobile ? '15px' : '20px',
                  fontSize: isMobile ? '12px' : '14px',
                  fontWeight: '600',
                  letterSpacing: '1px',
                  textTransform: 'uppercase',
                  cursor: (
                    (product?.weightOptions?.length > 0 && !selectedWeight) || 
                    (product?.typeOptions?.length > 0 && !selectedType)
                  ) ? 'not-allowed' : 'pointer',
                  opacity: (
                    (product?.weightOptions?.length > 0 && !selectedWeight) || 
                    (product?.typeOptions?.length > 0 && !selectedType)
                  ) ? 0.6 : 1
                }}
              >
                ADD TO CART - {getCurrentPrice()} QAR
              </button>
            </div>
          </div>

          {/* You Might Like These Section */}
          <div style={{ 
            marginTop: isMobile ? '40px' : '50px',
            borderTop: '1px solid #000',
            paddingTop: isMobile ? '30px' : '40px'
          }}>
            <h2 style={{
              fontSize: isMobile ? '20px' : '28px',
              fontWeight: '100',
              color: '#000',
              margin: isMobile ? '0 0 25px 0' : '0 0 30px 0',
              letterSpacing: isMobile ? '2px' : '3px',
              textTransform: 'uppercase',
              textAlign: 'center'
            }}>
              You Might Like These
            </h2>

            {/* Recommendations Grid */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : (() => {
                const availableProducts = allProducts.filter(coffee => coffee.name.toLowerCase().replace(/\s+/g, '-') !== id);
                const productCount = Math.min(availableProducts.length, 4);
                return `repeat(${productCount}, 1fr)`;
              })(),
              gap: '1px',
              border: '1px solid #000'
            }}>
              {allProducts
                .filter(coffee => coffee.name.toLowerCase().replace(/\s+/g, '-') !== id)
                .sort(() => 0.5 - Math.random())
                .slice(0, 4)
                .map((coffee, index) => (
                <div
                  key={coffee.id}
                  onClick={() => navigate(`/product/${coffee.name.toLowerCase().replace(/\s+/g, '-')}`)}
                  style={{
                    background: 'white',
                    borderRight: isMobile 
                      ? (index % 2 !== 1 ? '1px solid #000' : 'none')
                      : (() => {
                          const availableProducts = allProducts.filter(coffee => coffee.name.toLowerCase().replace(/\s+/g, '-') !== id);
                          const productCount = Math.min(availableProducts.length, 4);
                          return index % productCount !== productCount - 1 ? '1px solid #000' : 'none';
                        })(),
                    cursor: 'pointer',
                    transition: 'background-color 0.2s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#f5f5f5';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'white';
                  }}
                >
                  {/* Product Image */}
                  <div style={{
                    aspectRatio: '1/1',
                    overflow: 'hidden',
                    borderBottom: '1px solid #000'
                  }}>
                    <img
                      src={coffee.image}
                      alt={coffee.name}
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover'
                      }}
                    />
                  </div>

                  {/* Product Info */}
                  <div style={{ padding: isMobile ? '12px' : '12px' }}>
                    <div style={{
                      fontSize: isMobile ? '8px' : '9px',
                      fontWeight: '600',
                      color: '#000',
                      letterSpacing: '1px',
                      textTransform: 'uppercase',
                      marginBottom: isMobile ? '4px' : '6px'
                    }}>
                      {coffee.category === 'beans' ? 'Beans' : 
                       coffee.category === 'drip-bags' ? 'Drip Bags' : 
                       coffee.category}
                    </div>

                    <h3 style={{
                      fontSize: isMobile ? '14px' : '16px',
                      fontWeight: '300',
                      color: '#000',
                      margin: isMobile ? '0 0 6px 0' : '0 0 8px 0',
                      lineHeight: '1.2',
                      letterSpacing: '1px',
                      textTransform: 'uppercase'
                    }}>
                      {coffee.name}
                    </h3>
                    
                    <div style={{
                      fontSize: isMobile ? '9px' : '10px',
                      color: '#666',
                      margin: isMobile ? '0 0 10px 0' : '0 0 12px 0',
                      letterSpacing: '1px',
                      textTransform: 'uppercase'
                    }}>
                      {coffee.origin}
                    </div>

                    <div style={{
                      borderTop: '1px solid #000',
                      paddingTop: isMobile ? '10px' : '12px',
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center'
                    }}>
                      <span style={{
                        fontSize: isMobile ? '14px' : '16px',
                        fontWeight: '600',
                        color: '#000',
                        letterSpacing: '1px'
                      }}>
                        QAR {coffee.price}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default ProductDetailPage;